/**
 * What a skill is for, listed by the level it opens at.
 *
 * Everything the world knows how to make or gather with one skill, in the order
 * somebody meets it: the level, what it takes, what it gives, and where it
 * happens. Built from the definitions the client already holds, so a recipe
 * written in the tools turns up here the moment it is saved.
 *
 * Anything gated on something other than a level says so on its own line, and
 * anything marked hidden is left out: a surprise nobody should be able to read
 * about before they find it is not a guide entry.
 */

import { SkillIcon } from './SkillIcon';
import { CloseButton } from './CloseButton';
import { useDraggableWindow } from './draggable';
import { useAltHeld } from './alt-mode';
import { useWindowGrips } from './grips';
import type { GuideEntry } from './skill-guide';

export function SkillGuide({
  skill,
  level,
  entries,
  onClose,
}: {
  skill: string;
  level: number;
  entries: readonly GuideEntry[];
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown, nudge } = useDraggableWindow('skillguide');
  const altHeld = useAltHeld();
  const { size: windowSize, grips } = useWindowGrips('skillguide', nudge, altHeld, {
    width: 300,
    height: 240,
  });

  return (
    <div
      className={`trade skill-guide${altHeld ? ' ui-mode' : ''}${windowSize ? ' sized' : ''}`}
      ref={attach}
      style={{ ...style, ...(windowSize ?? {}) }}
    >
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span className="guide-title">
          {/* With the same picture the skills panel shows for it, so the guide
              is recognisably the one that was pressed. Named the way the panel
              names it: the skill arrives here as the word the world uses,
              which is lower case. */}
          <SkillIcon skill={skill} />
          {skill.slice(0, 1).toUpperCase() + skill.slice(1)} <b>{level}</b>
        </span>
        <CloseButton onClose={onClose} />
      </div>

      <div className="panel-body">
        {entries.length === 0 ? (
          <p className="hint">Nothing is written for this skill yet.</p>
        ) : null}

        {entries.map((entry, at) => (
          <div
            className={entry.reached ? 'guide-row' : 'guide-row short'}
            key={`${entry.name}-${entry.level}-${at}`}
          >
            <span className="guide-level">{entry.level}</span>

            <span className="guide-what">
              <b>{entry.name}</b>
              {entry.where ? <small>{entry.where}</small> : null}
              {entry.needs.length > 0 ? <small>{entry.needs.join(', ')}</small> : null}
            </span>

            <span className="guide-xp">{Math.round(entry.experience)} xp</span>
          </div>
        ))}
      </div>
      {grips}
    </div>
  );
}
